// Content Calendar v2 — mirrors the backend (creative-os-backend) response
// shapes. Every status / format is an application-level string union (no DB
// enums). See creative-os-backend/docs/content-calendar-plan.md.
//
// The model: per-brand settings drive a worker that plans WHOLE MONTHS. A month
// plan holds X slots (title / format / pillar / day). The operator approves the
// month, and 3 ideas are generated per slot.
//
// THE MONTH STOPS MOVING AS ONE UNIT THERE. From approval on, every POST
// travels on its own: sent to the client individually, decided individually
// (each answer acting on that post immediately), briefed and scheduled
// individually. See creative-os-backend/docs/per-slot-lifecycle-plan.md.

/**
 * The LIVE machine is short: DRAFT → GENERATING → APPROVED → ARCHIVED. There is
 * no approval step: a plan run lands the month in APPROVED, which is both where
 * a planned month starts and its terminal working state.
 *
 * PENDING_APPROVAL, and everything from IN_CLIENT_REVIEW to MONTH_SIGNED_OFF,
 * is LEGACY — values that shipped when the month moved as one unit, and when
 * the skeleton needed a person's sign-off. They still arrive on older rows and
 * behave exactly like APPROVED everywhere.
 */
export type CalendarPlanStatus =
  | "DRAFT"
  | "GENERATING"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "IN_CLIENT_REVIEW"
  | "CHANGES_REQUESTED"
  | "CLIENT_APPROVED"
  // The production-brief stage. CLIENT_APPROVED is a doorway, not the end.
  | "BRIEFS_GENERATING"
  | "BRIEFS_DRAFT"
  | "BRIEFS_IN_REVIEW"
  | "BRIEFS_CHANGES_REQUESTED"
  | "MONTH_SIGNED_OFF"
  | "ARCHIVED";

/** The retired statuses. Treated as APPROVED everywhere. */
export const LEGACY_PLAN_STATUSES: CalendarPlanStatus[] = [
  "PENDING_APPROVAL",
  "IN_CLIENT_REVIEW",
  "CHANGES_REQUESTED",
  "CLIENT_APPROVED",
  "BRIEFS_GENERATING",
  "BRIEFS_DRAFT",
  "BRIEFS_IN_REVIEW",
  "BRIEFS_CHANGES_REQUESTED",
  "MONTH_SIGNED_OFF",
];

/** "Planned and still live" — the per-slot regime's predicate. */
export const PLAN_APPROVED_LIKE_STATUSES: CalendarPlanStatus[] = [
  "APPROVED",
  ...LEGACY_PLAN_STATUSES,
];

export function isPlanApprovedLike(status: string): boolean {
  return (PLAN_APPROVED_LIKE_STATUSES as string[]).includes(status);
}

/**
 * Months the team may no longer RESHAPE (mirrors PLAN_TEAM_FROZEN_STATUSES).
 * ARCHIVED alone: what protects a post under a client's eyes is now the POST's
 * own status, so the month around it stays workable.
 */
export const PLAN_TEAM_FROZEN_STATUSES: CalendarPlanStatus[] = ["ARCHIVED"];

/**
 * Plan statuses the operator may hand-edit a production brief in — any live
 * planned month. The gate that actually bites is the BRIEF's own status.
 */
export const PLAN_BRIEF_EDITABLE_STATUSES: CalendarPlanStatus[] = [
  ...PLAN_APPROVED_LIKE_STATUSES,
];

/**
 * Is any post in this month at or beyond client concept approval?
 */
export function hasBriefStageSlots(
  slots: Array<{ status: CalendarSlotStatus }>,
): boolean {
  return slots.some((s) => (BRIEF_STAGE_STATUSES as readonly string[]).includes(s.status));
}

/** A post has (or is getting) its creative brief from the pick on: the idea and the brief go out together. */
export const BRIEF_STAGE_STATUSES = ["SIGNED_OFF", "IN_CLIENT_REVIEW", "CLIENT_APPROVED"] as const satisfies readonly CalendarSlotStatus[];

export function slotHasBrief(slot: { status: CalendarSlotStatus; origin?: string }): boolean {
  return slot.origin !== "DIRECT" && (BRIEF_STAGE_STATUSES as readonly string[]).includes(slot.status);
}

/**
 * Status sets for the poll predicates (content-calendar/api/planPolling.ts).
 * Exported constants rather than literals in the hooks: the detail poll and the
 * month-list poll drifted apart once already, and the symptom is a month that
 * keeps claiming to be working until someone reloads.
 *
 * Only plan generation is a month-level machine state now. Everything else that
 * moves — a post researching, a brief being written, a publication going out —
 * is per-post, and the SLOT predicates below are what keep the poll honest.
 */
export const PLAN_MACHINE_STATUSES: CalendarPlanStatus[] = ["GENERATING", "BRIEFS_GENERATING"];

/** A month where posts are with the clients — their decisions land server-side. */
export const PLAN_FEEDBACK_STATUSES: CalendarPlanStatus[] = [
  "IN_CLIENT_REVIEW",
  "BRIEFS_IN_REVIEW",
];

/**
 * Statuses in which a PLANNED slot is a QUEUE, not a resting place: the plan
 * run's own fan-out, decline rework, daily ideation cron and recovery cron all
 * claim PLANNED slots while the plan sits in one of these.
 */
export const PLAN_IDEATION_STATUSES: CalendarPlanStatus[] = [...PLAN_APPROVED_LIKE_STATUSES];

/**
 * The one status nothing ever moves again. MONTH_SIGNED_OFF is deliberately NOT
 * here: a teammate can re-plan a signed-off month from another tab, and the
 * nightly rollover archives it — both invisible to a stopped poll, so
 * signed-off months keep the idle floor.
 */
export const PLAN_TERMINAL_STATUSES: CalendarPlanStatus[] = ["ARCHIVED"];

export function isPlanTerminal(status: string): boolean {
  return (PLAN_TERMINAL_STATUSES as string[]).includes(status);
}

export function isPlanFrozen(status: string): boolean {
  return (PLAN_TEAM_FROZEN_STATUSES as string[]).includes(status);
}

/**
 * No pitch stage: `IDEATION` is where the operator reviews the three ideas AND
 * signs one off. From SIGNED_OFF on, the slot carries the whole client
 * lifecycle — this is where the per-slot regime lives.
 */
export type CalendarSlotStatus =
  | "PLANNED"
  | "GENERATING_IDEAS"
  | "IDEATION"
  /** The team chose the idea; its draft is not yet shared. */
  | "SIGNED_OFF"
  /** The chosen concept version is with the client. */
  | "IN_CLIENT_REVIEW"
  /** The concept is approved; creative brief development and review can begin. */
  | "CLIENT_APPROVED"
  | "MISSED";

/** Status totals returned with each row from the month-plan list endpoint. */
export type CalendarSlotCounts = { [Status in CalendarSlotStatus]: number };

/** Statuses with a background run in flight (drives polling/spinners). */
export const GENERATING_SLOT_STATUSES: CalendarSlotStatus[] = ["GENERATING_IDEAS"];

/** Posts whose CONTENT is settled — only the day may still move (and not in review). */
export const SLOT_CONTENT_FINAL_STATUSES: CalendarSlotStatus[] = [
  "SIGNED_OFF",
  "IN_CLIENT_REVIEW",
  "CLIENT_APPROVED",
];

/**
 * Who took a post past the client's idea gate. CLIENT is a decision made on the
 * client's own board; TEAM is the bypass an operator takes when there is nobody
 * outside the building to wait on. Null until the post is approved, and cleared
 * again when a decline sends it back.
 */
export type SlotApprovalSource = "CLIENT" | "TEAM";

/**
 * The same question for a production brief. It lives beside the slot's own
 * source rather than with the brief types because one pair of bypass endpoints
 * writes both, and the two answers are read together.
 */
export type BriefAcceptanceSource = "CLIENT" | "TEAM";

/**
 * How a post came to exist. A DIRECT ("quick") post is ready-made content the
 * team dropped straight onto a day: no ideas, no brief, no client review, and
 * schedulable the moment its media lands. Every behavioural difference keys on
 * this, never on the status.
 */
export type SlotOrigin = "PIPELINE" | "DIRECT";

export function isDirectSlot(slot: { origin?: SlotOrigin }): boolean {
  return slot.origin === "DIRECT";
}

/** Posts the operator may send to the client right now. */
export function isSendableSlot(slot: {
  status: CalendarSlotStatus;
  origin?: SlotOrigin;
  ideas?: Array<{ status: CalendarIdeaStatus }>;
}): boolean {
  if (isDirectSlot(slot)) return false;
  if (slot.status !== "SIGNED_OFF") return false;
  return (slot.ideas ?? []).some((i) => i.status === "ACCEPTED");
}

// ---- Production format ----
//
// Media type and exact aspect ratio are independent: VIDEO · 4:5 is as valid as
// VIDEO · 9:16. Orientation remains in the API as a derived compatibility axis
// for the existing cadence buckets; never use it as the visible format label.

/** What a planned post is. Only VIDEO carries a length band and a duration. */
export type CalendarMediaType = "VIDEO" | "STATIC";
export const CALENDAR_MEDIA_TYPES: CalendarMediaType[] = ["VIDEO", "STATIC"];
export const MEDIA_TYPE_LABEL: Record<CalendarMediaType, string> = {
  VIDEO: "Video",
  STATIC: "Static",
};

/** Which way up the frame is. Applies to video AND static alike. */
export type CalendarOrientation = "HORIZONTAL" | "VERTICAL";
export const CALENDAR_ORIENTATIONS: CalendarOrientation[] = ["VERTICAL", "HORIZONTAL"];
export const ORIENTATION_LABEL: Record<CalendarOrientation, string> = {
  VERTICAL: "Vertical",
  HORIZONTAL: "Horizontal",
};

/** The runtime band a video slot sits in. Null on a static slot. */
export type CalendarVideoLength = "SHORT_FORM" | "LONG_FORM";
export const CALENDAR_VIDEO_LENGTHS: CalendarVideoLength[] = ["SHORT_FORM", "LONG_FORM"];
export const VIDEO_LENGTH_LABEL: Record<CalendarVideoLength, string> = {
  SHORT_FORM: "Short form",
  LONG_FORM: "Long form",
};

/** The concrete production canvas. Older ratios remain readable. */
export type AspectRatio = "16:9" | "4:3" | "9:16" | "3:4" | "4:5" | "1:1";

/** Which ratios an orientation allows — the whole constraint, in one map. */
export const ASPECT_RATIOS_BY_ORIENTATION: Record<CalendarOrientation, AspectRatio[]> = {
  HORIZONTAL: ["16:9", "4:3"],
  VERTICAL: ["9:16", "4:5", "3:4", "1:1"],
};

export const ALL_ASPECT_RATIOS: AspectRatio[] = ["9:16", "4:5", "1:1", "3:4", "16:9", "4:3"];

/** Ratios offered for new work; the legacy values above remain display-safe. */
export const SLOT_ASPECT_RATIOS_BY_MEDIA_TYPE: Record<CalendarMediaType, AspectRatio[]> = {
  STATIC: ["4:5", "1:1"],
  VIDEO: ["9:16", "4:5", "16:9"],
};

export function defaultAspectRatio(
  mediaType: CalendarMediaType,
  orientation: CalendarOrientation = "VERTICAL",
): AspectRatio {
  if (mediaType === "STATIC") return "4:5";
  return orientation === "HORIZONTAL" ? "16:9" : "9:16";
}

export function orientationForAspectRatio(ratio: AspectRatio): CalendarOrientation {
  return ratio === "16:9" || ratio === "4:3" ? "HORIZONTAL" : "VERTICAL";
}

/** Only a video slot carries a length band and a duration. */
export function mediaTypeHasDuration(mediaType: CalendarMediaType): boolean {
  return mediaType === "VIDEO";
}

/** "Video · 4:5 · Short form" / "Static · 1:1" — production-ready language. */
export function formatLabel(slot: {
  mediaType: CalendarMediaType;
  orientation: CalendarOrientation;
  aspectRatio?: AspectRatio;
  videoLength: CalendarVideoLength | null;
  ideas?: Array<{ aspectRatio: AspectRatio; status?: string }>;
}): string {
  const ratio = displayAspectRatio(slot);
  const format = `${MEDIA_TYPE_LABEL[slot.mediaType]} · ${ratio}`;
  if (!slot.videoLength) return format;
  return `${format} · ${VIDEO_LENGTH_LABEL[slot.videoLength]}`;
}

/** Safe during the backend rollout and for rows created before the migration. */
export function displayAspectRatio(slot: {
  mediaType: CalendarMediaType;
  orientation: CalendarOrientation;
  aspectRatio?: AspectRatio;
  ideas?: Array<{ aspectRatio: AspectRatio; status?: string }>;
}): AspectRatio {
  if (slot.aspectRatio) return slot.aspectRatio;
  const accepted = slot.ideas?.find((idea) => idea.status === "ACCEPTED")?.aspectRatio;
  return accepted ?? slot.ideas?.[0]?.aspectRatio ?? defaultAspectRatio(slot.mediaType, slot.orientation);
}

/** The duration band, in seconds — mirrors the backend's ideationConfig. */
export const SHORT_FORM_MAX_SECONDS = 359;
export const LONG_FORM_MIN_SECONDS = 360;
export const MIN_DURATION_SECONDS = 5;
export const MAX_DURATION_SECONDS = 3600;

export function durationBoundsFor(
  videoLength: CalendarVideoLength | null,
): [number, number] {
  return videoLength === "LONG_FORM"
    ? [LONG_FORM_MIN_SECONDS, MAX_DURATION_SECONDS]
    : [MIN_DURATION_SECONDS, SHORT_FORM_MAX_SECONDS];
}

export type CalendarIdeaStatus = "PROPOSED" | "WORKING" | "ACCEPTED";
export type IdeaSourceType = "trend" | "signal" | "universe" | "web" | "media" | "reference";

/** "Where this came from" — a grounding link, verified server-side. */
export interface IdeaSource {
  type: IdeaSourceType;
  refId?: string;
  label: string;
  url?: string;
}

/**
 * The ratio the reference frame takes inside the fixed-height well. Reuses the
 * same vocabulary as the idea's own aspect ratio — a frame shape is a frame
 * shape, and two enums for it would only invite them to drift.
 */
export type IdeaReferenceType = AspectRatio;

/**
 * Proposal media is independent from the authored concept. It can come from a
 * trend example, a saved reference, the team's brand-media library, or a custom
 * link pasted for this concept alone; playable URLs are resolved fresh on every
 * read while the stored label and source ratio survive if the original item
 * later disappears.
 */
export interface IdeaReference {
  kind?: "trend" | "reference" | "media" | "custom";
  type: IdeaReferenceType;
  mediaType?: "video" | "image";
  platform: string;
  label: string;
  videoUrl: string | null;
  imageUrl?: string | null;
  pageUrl: string | null;
  thumbnailUrl: string | null;
  /**
   * Every file of a custom link, in order. More than one is a carousel to page
   * through; the fields above always describe the first. Empty for the three
   * libraries.
   */
  items?: ReferenceMediaItem[];
  /** The row behind it, by kind: Change media marks it picked. */
  trendId?: string;
  trendExampleId?: string;
  referenceId?: string;
  mediaId?: string;
  customMediaId?: string;
}

/** One file of a multi-file post, with URLs good for about an hour. */
export interface ReferenceMediaItem {
  mediaType: "video" | "image";
  url: string | null;
  /** A video file's cover frame. */
  posterUrl: string | null;
}

/** A team choice that replaces only an idea's proposal media. */
export type IdeaMediaPick =
  | { kind: "trend"; trendExampleId: string }
  | { kind: "reference"; referenceId: string }
  | { kind: "media"; mediaId: string }
  | { kind: "custom"; customMediaId: string }
  | { kind: "none" };

export type CustomMediaStatus = "PROCESSING" | "READY" | "FAILED";

/**
 * A link pasted as one-off proposal media or a shot's visual. Saved to our own
 * storage but in no library — reachable only through the pick that uses it.
 * YouTube keeps only its thumbnail (`items` is empty) and plays on YouTube.
 */
export interface CustomMedia {
  id: string;
  platform: "instagram" | "tiktok" | "youtube";
  status: CustomMediaStatus;
  /** Why the save failed, written for the person who pasted the link. */
  error: string | null;
  sourceUrl: string;
  pageUrl: string | null;
  label: string;
  authorHandle: string | null;
  /** Set once READY. */
  mediaType: "video" | "image" | "carousel" | null;
  aspectRatio: string | null;
  videoUrl: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  items: ReferenceMediaItem[];
  createdAt: string;
}

/** Who authored an idea comment (team member vs external brand client). */
export type CommentAuthorType = "TEAM" | "CLIENT";
export type ClientIdeaReaction = "ACCEPTED" | "DECLINED";

/**
 * The part of the post a comment is pinned to ("the hook", "shot 03").
 * backend #128: not in the API yet; the rail shows it only when present.
 */
export interface CommentAnchor {
  /** Which part of the page, as the API names it. */
  part: string;
  /** How the rail names it after "Concept ·" or "Creative brief ·". */
  label: string;
  /** The thread's marker number on the page. */
  number?: number;
}

/**
 * A change M7 drafted or suggested from a comment.
 * backend #125: not in the API yet; the rail shows it only when present.
 */
export interface CommentM7Change {
  state: "suggested" | "accepted";
  /** The block's label as the API writes it ("M7 changed the hook"). */
  label: string;
  body: string;
  /** A line under an accepted change ("Accepted · goes out in version 2"). */
  note?: string | null;
}

/**
 * Thread fields the API does not send yet. Each is optional and the rail
 * renders its piece only when the field is present (comments/commentCapabilities).
 */
export interface CommentThreadExtras {
  /** backend #128 */
  anchor?: CommentAnchor | null;
  /** backend #128: replies on this comment, oldest first. */
  replies?: IdeaComment[];
  /** backend #128: when the thread was marked done. Present (even null) only once the API supports resolving. */
  resolvedAt?: string | null;
  /** backend #125 */
  m7Change?: CommentM7Change | null;
}

/** A comment on an idea thread (shared between the team and the brand's clients). */
export interface IdeaComment extends CommentThreadExtras {
  id: string;
  authorType: CommentAuthorType;
  authorName: string;
  body: string;
  createdAt: string;
  /** Concept-version comments carry it; SHARED when absent. */
  visibility?: "SHARED" | "INTERNAL";
}

/** A client's accept/decline reaction on an idea, as seen on the team side. */
export interface IdeaReaction {
  id: string;
  reaction: ClientIdeaReaction;
  client: { id: string; name: string | null; email: string };
  createdAt: string;
}

/** One of the 3 proposed ideas. Every field is a row on the operator card. */
/**
 * How many opening lines one concept may carry. Mirrors HOOK_OPTIONS_MAX on the
 * backend, which re-clamps anyway — this only stops the editor offering a row
 * the server would drop.
 */
export const HOOK_OPTIONS_MAX = 5;

export interface CalendarSlotIdea {
  id: string;
  slotId: string;
  /** The name the shot list uses — frozen once the slot is signed off. */
  title: string;
  /** One sentence a producer can shoot from. */
  synopsis: string;
  /** A set, never a single line. The client card shows hooks[0]. */
  hooks: string[];
  /** Only when a source is a trend; else null. */
  reference: IdeaReference | null;
  sources: IdeaSource[];
  /** The link back to the slot's pillar, written as a disagreeable claim. */
  objectiveClaim: string;
  /** Where it runs. Editable inline. */
  channels: CalendarPlatform[];
  /** The exact frame — always one of the two its slot's orientation allows. */
  aspectRatio: AspectRatio;
  /** Video formats only — always null on a static. */
  durationSeconds: number | null;
  status: CalendarIdeaStatus;
  order: number;
  /** Audit: who signed this idea off, and when. */
  decidedByUserId: string | null;
  decidedAt: string | null;
  /** Client feedback (team-facing reads only). Reactions exclude revoked clients. */
  comments?: IdeaComment[];
  reactions?: IdeaReaction[];
  createdAt: string;
  updatedAt: string;
}

/**
 * The product's channel vocabulary — an idea's channels and the strategy's
 * channels are the same set (mirrors the backend CalendarPlatform enum in
 * utils/enums/ContentCalendar.ts; keep the two in step).
 */
export type CalendarPlatform =
  | "TIKTOK"
  | "INSTAGRAM"
  | "YOUTUBE"
  | "FACEBOOK"
  | "SNAPCHAT"
  | "LINKEDIN"
  | "PINTEREST"
  | "REDDIT"
  | "SUBSTACK";

/** All channels, in canonical display order (must match the backend's order). */
export const CALENDAR_PLATFORMS: CalendarPlatform[] = [
  "TIKTOK",
  "INSTAGRAM",
  "YOUTUBE",
  "FACEBOOK",
  "SNAPCHAT",
  "LINKEDIN",
  "PINTEREST",
  "REDDIT",
  "SUBSTACK",
];

// ---- The client review round ----

export type CalendarReviewRoundStatus = "PENDING" | "APPROVED" | "CHANGES_REQUESTED";
export type CalendarReviewDecisionValue = "APPROVED" | "DECLINED";

/**
 * A decline must say why, in at least this many characters. The reason is fed
 * verbatim into the prompt that generates the replacements — mirrors
 * REVIEW_DECLINE_REASON_MIN_LENGTH on the backend.
 */
export const REVIEW_DECLINE_REASON_MIN_LENGTH = 20;
export const REVIEW_DECLINE_REASON_MAX_LENGTH = 2000;

/** A comment frozen with the reviewed concept so its discussion survives rework. */
export interface ReviewCommentSnapshot {
  authorType: CommentAuthorType;
  authorName: string;
  body: string;
}

/** The reviewed idea, frozen at decision time — survives the rework that wipes it. */
export interface ReviewIdeaSnapshot {
  id: string;
  title: string;
  synopsis: string;
  hooks: string[];
  objectiveClaim: string;
  channels: CalendarPlatform[];
  aspectRatio: AspectRatio;
  durationSeconds: number | null;
  reference?: IdeaReference | null;
  sources?: IdeaSource[];
  scheduledAt?: string;
  pillarName?: string | null;
  mediaType?: CalendarMediaType;
  videoLength?: CalendarVideoLength | null;
  /** Discussion attached to the reviewed version, when the API supplies it. */
  comments?: ReviewCommentSnapshot[];
}

/** Client versions are created only by an explicit send, never by draft saves. */
export interface ConceptVersion {
  id: string;
  slotId: string;
  roundId: string;
  ideaId: string;
  version: number;
  sharedAt: string;
  sharedByUserId: string;
  alignmentGuidance: string | null;
  isLatest: boolean;
  readOnly: boolean;
  /** Null when the stored snapshot could not be read — render from the slot. */
  snapshot: ReviewIdeaSnapshot | null;
  comments: (IdeaComment & { visibility?: "SHARED" | "INTERNAL" })[];
  decision: { decision: CalendarReviewDecisionValue; reason: string | null; clientName: string; decidedAt: string } | null;
}

export interface ConceptVersionsResponse {
  latestSharedVersionId: string | null;
  currentDraft: (Omit<ReviewIdeaSnapshot, "id"> & { ideaId: string }) | null;
  alignment: { guidance: string; alignedAt: string | null; alignedByUserId: string | null } | null;
  versions: ConceptVersion[];
}

/** The client's verdict on one post, in one round. */
export interface CalendarReviewDecision {
  id: string;
  roundId: string;
  slotId: string;
  ideaId: string;
  ideaSnapshot: ReviewIdeaSnapshot;
  decision: CalendarReviewDecisionValue;
  /** Present on a decline; this is what the rework is briefed with. */
  reason: string | null;
  clientId: string | null;
  clientName: string;
  /** Carried forward from the previous round — the post didn't change. */
  carriedOver: boolean;
  decidedAt: string;
  /** Included on the per-slot decision so the banner can name the round. */
  round?: { roundNumber: number; status: CalendarReviewRoundStatus };
}

/** One pass of the month in front of the client. */
export interface CalendarReviewRound {
  id: string;
  planId: string;
  roundNumber: number;
  status: CalendarReviewRoundStatus;
  sentAt: string;
  sentByUserId: string;
  notifiedCount: number;
  closedAt: string | null;
  decisions: CalendarReviewDecision[];
}

/** A planned post: the brief the ideas are generated against. */
export interface CalendarPlanSlot {
  id: string;
  planId: string;
  /** The post's slides (month read only): 2 or more make it a carousel. */
  itemCount?: number;
  /** The plan-grid label. */
  title: string;
  mediaType: CalendarMediaType;
  orientation: CalendarOrientation;
  /** Exact production canvas. */
  aspectRatio: AspectRatio;
  /** Always null on a STATIC slot. */
  videoLength: CalendarVideoLength | null;
  pillarId: string | null;
  /** Snapshot of the pillar name at plan time; survives pillar deletion. */
  pillarName: string;
  /** ISO timestamp — always 00:00 UTC. v2 is day-granular. */
  scheduledAt: string;
  order: number;
  status: CalendarSlotStatus;
  /** Who cleared the idea gate, and who they were. Null until CLIENT_APPROVED. */
  approvalSource: SlotApprovalSource | null;
  approvedByUserId: string | null;
  /** PIPELINE by default; DIRECT marks a quick post (no ideas, no brief). */
  origin: SlotOrigin;
  /** DIRECT posts only — the platforms its publications target. */
  channels: string[];
  ideaAttempts: number;
  ideaError: string | null;
  /** The 3 AI-proposed ideas (present from IDEATION onwards). */
  ideas?: CalendarSlotIdea[];
  /**
   * The client's most recent verdict on this post — at most one entry. A DECLINED
   * one is what the rework banner reads: it carries the reason AND a snapshot of
   * the idea that was rejected, which no longer exists anywhere else.
   */
  reviewDecisions?: CalendarReviewDecision[];
  /** Scheduling stage: the uploaded asset + per-platform publish state (badges). */
  deliverable?: SlotDeliverableSummary | null;
  /** The post's open at-risk warning from the daily check (backend #126), or null. */
  atRisk?: CalendarPostRisk | null;
  /** The open concept and brief sends this post waits in (backend #130), team read only. */
  clientReview?: { concept: SlotClientReview | null; brief: SlotClientReview | null };
  /** Approvals the client gave outside CreativeOS, as the team recorded them (backend #129). */
  outsideApproval?: { concept: SlotOutsideApproval | null; brief: SlotOutsideApproval | null };
  /** When the team moved the post into production, and who (plan scalars). */
  productionStartedAt?: string | null;
  productionStartedByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The last reminder sent for an open send (backend ReminderView). */
export interface ClientReminder {
  sentAt: string;
  automatic: boolean;
  byName: string | null;
  recipients: number;
}

/** One post's open send to the client (backend #130, SlotClientReview). */
export interface SlotClientReview {
  roundId: string;
  /** When the send went to the client. */
  sentAt: string;
  /** Start of the answer-by day, or null when none was asked. */
  answerBy: string | null;
  note: string | null;
  /** The answer-by day passed and the client has not answered. */
  late: boolean;
  lastReminder: ClientReminder | null;
  /** When a client first opened this send of the post, and who (concept sends). */
  openedAt?: string | null;
  openedByName?: string | null;
}

export type OutsideApprovalChannel = "whatsapp" | "email" | "call" | "in_person";

/** An approval given outside CreativeOS (backend #129). */
export interface SlotOutsideApproval {
  approvedByName: string;
  channel: OutsideApprovalChannel;
  approvedAt: string;
  note: string | null;
}

/** The one next step a risk suggests (backend PostRiskFix). */
export type PostRiskFix = "pick" | "send" | "remind" | "send_brief" | "move" | "ask_owner";

/** A post at risk of missing its day (backend #126, riskView). */
export interface CalendarPostRisk {
  id: string;
  slotId: string;
  risk: string;
  severity: "warning" | "critical";
  /** One plain sentence, every date and count from the data. */
  reason: string;
  fix: { kind: PostRiskFix; day: string | null };
  status: "open" | "accepted" | "dismissed";
  detectedAt: string;
}

/** The month-grid badge payload — mirror of the team read's deliverable include. */
export interface SlotDeliverableSummary {
  status: "UPLOADING" | "PROCESSING" | "READY" | "FAILED";
  publications: {
    platform: CalendarPlatform;
    status: PublicationBadgeStatus;
    scheduledAt: string;
    permalink: string | null;
    /**
     * When the dispatcher will try a FAILED publication again, null when it has
     * given up. Always present — the team read selects it (backend #68) — so a
     * FAILED row with a backoff still to run is distinguishable from a terminal
     * one without opening the distribution endpoint.
     */
    nextAttemptAt: string | null;
    /** When it went live; null until PUBLISHED. */
    publishedAt?: string | null;
  }[];
}

export type PublicationBadgeStatus =
  | "DRAFT"
  | "READY"
  | "SCHEDULED"
  | "PUBLISHING"
  | "PUBLISHED"
  | "FAILED"
  | "CANCELLED";

/** One month of planned content — the approval unit. */
export interface CalendarMonthPlan {
  id: string;
  brandId: string;
  year: number;
  /** 1-12. */
  month: number;
  status: CalendarPlanStatus;
  /** How many slots this month was planned for (pro-rated for a partial month). */
  targetSlotCount: number;
  /**
   * The format mix this month was CLAIMED for, snapshotted from the brand's
   * cadence at generation time. Always sums to `targetSlotCount`.
   *
   * These ship on every plan read — `listByBrand` is a bare `findMany` with no
   * `select` — they were simply never declared here, so the list endpoint's own
   * scope contract was invisible to the client and every surface that wanted it
   * re-derived it from the brand's LIVE settings instead. That is the one thing
   * the snapshot exists to prevent: a cadence change must not move the target a
   * finished month is reported against.
   */
  targetStaticCount: number;
  targetVerticalVideoCount: number;
  targetHorizontalVideoCount: number;
  /**
   * True when someone set THIS month's cadence by hand. The three targets are
   * then the month's own numbers — exact, never pro-rated — and a re-plan keeps
   * them instead of re-reading the brand's cadence.
   */
  hasCustomCadence: boolean;
  strategyId: string | null;
  generationAttempts: number;
  generationError: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The list endpoint enriches every plan row with one grouped status count map. */
export interface CalendarMonthPlanListRow extends CalendarMonthPlan {
  slotCounts: CalendarSlotCounts;
}

export interface CalendarMonthPlanWithSlots extends CalendarMonthPlan {
  slots: CalendarPlanSlot[];
  /** The latest review round only — the month banner's whole input. */
  reviewRounds?: CalendarReviewRound[];
}

/** The month's current round, or null if it was never sent. */
export function latestReviewRound(
  plan: CalendarMonthPlanWithSlots | null | undefined,
): CalendarReviewRound | null {
  return plan?.reviewRounds?.[0] ?? null;
}

/** The client's latest verdict on a post, or null. */
export function latestReviewDecision(
  slot: CalendarPlanSlot | null | undefined,
): CalendarReviewDecision | null {
  return slot?.reviewDecisions?.[0] ?? null;
}

/**
 * The decline a post is currently being reworked because of — i.e. a decline
 * that is still the last word on it. Once the post is signed off again the
 * decision stays in the record but stops being the thing to act on.
 */
export function openDeclineFor(
  slot: CalendarPlanSlot | null | undefined,
): CalendarReviewDecision | null {
  const decision = latestReviewDecision(slot);
  if (!decision || decision.decision !== "DECLINED") return null;
  // Once the post has been signed off again (or re-sent, or approved), the
  // decline has been answered and stops being the thing to shout about.
  return slot && (SLOT_CONTENT_FINAL_STATUSES as string[]).includes(slot.status)
    ? null
    : decision;
}

/**
 * The brand's planning cadence. The three format counts ARE the cadence — there
 * is no separate monthly total, it is their sum (see `totalPostsPerMonth`).
 * Each is an exact target the planner is held to, and 0 is a valid answer: it is
 * how a brand says "we don't produce 16:9 video".
 */
export interface BrandCalendarSettings {
  id: string;
  brandId: string;
  /** 1 | 2 | 3 — how many months ahead one planning run reaches. */
  planningPeriodMonths: number;
  /** Designed images / carousels. Exact ratio is chosen per post, not budgeted. */
  staticPostsPerMonth: number;
  /** Feed-native video — the exact post ratio is 9:16 or 4:5. */
  verticalVideoPostsPerMonth: number;
  /** 16:9 video — YouTube, site embeds, presentations. */
  horizontalVideoPostsPerMonth: number;
  autoPlanEnabled: boolean;
  /** The day of the month automatic planning runs on (1-28), or null for the default. */
  planDayOfMonth?: number | null;
  /** A scheduled run also writes the three ideas for each post it lays out (Owner-only to change). */
  writeIdeasWithPlan?: boolean;
  /** Reply by: the client's default answer-by day, this many days before the post; null for none. */
  replyByDaysBefore?: number | null;
  /** Who can send ideas and briefs to the client (Owner-only to change). Missing = ANYONE. */
  clientSendPolicy?: CalendarClientSendPolicy;
  /** Where the three counts came from: "STRATEGY" | "PERSON", null while still the defaults. */
  scopeSource?: string | null;
  /** When someone (or a strategy going active) first set the settings on purpose. */
  confirmedAt?: string | null;
  /** IANA zone the brand's publish times are picked in ("Asia/Dubai"). */
  timezone: string;
  /** "HH:mm" 24h, brand-local default publish time for new publications. */
  defaultPublishTime: string;
  /**
   * LEGACY — has no effect. Capped the automatic re-pitches a client's decline
   * used to trigger; a decline now waits for a person to regenerate. Still on
   * the row until the column is dropped.
   */
  maxClientReworksPerSlot: number;
  createdAt: string;
  updatedAt: string;
}

export const PLANNING_PERIOD_CHOICES = [1, 2, 3] as const;
/** The ceiling on the whole month, across all three formats. */
export const MAX_POSTS_PER_MONTH = 30;
export const MIN_POSTS_PER_MONTH = 1;

/**
 * The one-off brief a manual plan run can carry. Mirrors planConfig.ts on the
 * backend, which enforces both — these only exist to fail the form early.
 */
export const PLAN_GUIDANCE_MAX_CHARS = 4000;
export const PLAN_REFERENCE_MAX_MB = 10;

/** A PDF sent inline with one plan run. Read during the run, then discarded. */
export interface PlanReferenceDocument {
  fileName: string;
  mediaType: "application/pdf";
  base64: string;
}

/**
 * The operator's one-off direction for a single run. Carried by both "plan this
 * month" and "re-plan it"; empty is valid and means "no brief".
 */
export interface PlanBriefInput {
  guidance?: string;
  referenceDocument?: PlanReferenceDocument;
}

/** The month's post count: the format mix summed. */
export function totalPostsPerMonth(mix: {
  staticPostsPerMonth: number;
  verticalVideoPostsPerMonth: number;
  horizontalVideoPostsPerMonth: number;
}): number {
  return (
    Math.max(0, mix.staticPostsPerMonth) +
    Math.max(0, mix.verticalVideoPostsPerMonth) +
    Math.max(0, mix.horizontalVideoPostsPerMonth)
  );
}

/**
 * Outcome of putting posts on the client's board. No email counts: sharing is
 * silent, and notifying is its own call.
 */
export interface SendForReviewResult {
  plan: CalendarMonthPlanWithSlots;
  round: { number: number; awaiting: number };
  /** The combined send emails in the same call (send-posts): who was reached. */
  email?: { notified: number; failed: number; failures: { userId: string; email: string }[] } | null;
}

/**
 * One of the brand's guests (its clients) and their standing with this month,
 * for the Notify dialog.
 */
export interface GuestNotifyTarget {
  userId: string;
  email: string;
  name: string | null;
  /** ISO timestamp of the last email about this month, or null if never. */
  lastNotifiedAt: string | null;
  /** ISO timestamp of the last time they opened this month, or null if never. */
  lastViewedAt: string | null;
}

/** What an operator is about to send, and to whom — the Notify dialog's preflight. */
export interface ClientNotifyState {
  monthLabel: string;
  awaitingPosts: number;
  awaitingBriefs: number;
  approvedPosts: number;
  /** The brand's guests — the only people a month is emailed to. */
  guests: GuestNotifyTarget[];
}

export interface NotifyClientsResult {
  notified: number;
  failed: number;
  failures: Array<{ userId: string; email: string }>;
  state: ClientNotifyState;
}

/**
 * Outcome of approving an idea. Approving never sends the month anywhere —
 * even the last rework — so this is just the updated slot.
 */
export interface AcceptIdeaResult {
  slot: CalendarPlanSlot;
}

// --- Mutation payloads ---

/** The three counts that ARE a cadence, at brand level or for one month. */
export interface CadenceCounts {
  staticPostsPerMonth: number;
  verticalVideoPostsPerMonth: number;
  horizontalVideoPostsPerMonth: number;
}

/**
 * ONE month's cadence: pin it to exact numbers, or hand it back to the brand's
 * cadence. All three counts travel together — a pinned month is a statement,
 * not a partial edit.
 */
export type MonthCadenceInput = CadenceCounts | { useBrandCadence: true };

export interface CalendarSettingsInput {
  planningPeriodMonths?: number;
  staticPostsPerMonth?: number;
  verticalVideoPostsPerMonth?: number;
  horizontalVideoPostsPerMonth?: number;
  autoPlanEnabled?: boolean;
  timezone?: string;
  defaultPublishTime?: string;
  /** Owner-only; the server rejects it from anyone else. */
  maxClientReworksPerSlot?: number;
  /** The brand-local day (1-28) the far month is planned on; null plans it as soon as it is in range. */
  planDayOfMonth?: number | null;
  /** Owner-only, like the reworks cap: a scheduled run also writes ideas. */
  writeIdeasWithPlan?: boolean;
  /** Reply by: N days before the post (0-30), or null for no default. */
  replyByDaysBefore?: number | null;
  /** Owner-only: who can send to the client. */
  clientSendPolicy?: CalendarClientSendPolicy;
}

/** Who can send ideas and briefs to the client: anyone on the team, or Owners only. */
export type CalendarClientSendPolicy = "ANYONE" | "OWNERS";

export interface PlanSlotInput {
  title: string;
  mediaType: CalendarMediaType;
  aspectRatio: AspectRatio;
  /** Derived from aspectRatio; retained while the cadence API uses it. */
  orientation: CalendarOrientation;
  videoLength?: CalendarVideoLength | null;
  pillarId?: string | null;
  /** ISO day; any time component is dropped server-side. */
  scheduledAt: string;
  /**
   * The platforms the post targets. Edit-only: on a DIRECT post it patches the
   * slot, on a pipeline post the server routes it to the signed-off idea.
   */
  channels?: CalendarPlatform[];
}

/** Operator inline edits on an idea card. */
/**
 * A concept the team writes by hand — POST .../ideas/manual. On a post with no
 * ideas, or beside the three (Figma 08 "Write your own idea"), where it is the pick.
 */
export interface ManualIdeaInput {
  title: string;
  synopsis: string;
  hooks: string[];
  objectiveClaim?: string;
  channels?: CalendarPlatform[];
  durationSeconds?: number | null;
  /** Up to four, picked from References or pasted; the first is the idea's reference. */
  references?: Exclude<IdeaMediaPick, { kind: "none" }>[];
}

export interface IdeaEditInput {
  title?: string;
  synopsis?: string;
  hooks?: string[];
  objectiveClaim?: string;
  channels?: CalendarPlatform[];
  aspectRatio?: AspectRatio;
  durationSeconds?: number | null;
}
